import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2, UserPlus } from 'lucide-react';
import { api, spaceErrorMessage, spacesApi, type SpaceGrantView, type User } from '../../lib/api';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input, Select } from '../ui/input';

type GrantAccess = SpaceGrantView['access'];

const dateOnly = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

function personLabel(grant: Pick<SpaceGrantView, 'name' | 'email' | 'userId'>): string {
  return grant.name?.trim() || grant.email || grant.userId;
}

function Expiry({ grant }: { grant: SpaceGrantView }) {
  if (!grant.expiresAt) return <span className="text-[10px] text-ink-faint">no expiry</span>;
  const when = dateOnly.format(new Date(grant.expiresAt));
  return grant.expired ? (
    <Badge variant="danger" className="px-1.5 py-0 text-[10px]">expired {when}</Badge>
  ) : (
    <span className="text-[10px] text-ink-faint">until {when}</span>
  );
}

/**
 * A date picked as "until the 30th" means through the 30th, so it is sent as the end of that day in the
 * person's own zone rather than as its first instant — which would end access a day early.
 */
function endOfDay(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

function todayInput(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * Who can open a folder, and changing that.
 *
 * Direct grants are this folder's own and can be changed here; inherited ones come from a folder above
 * it and are shown so the list answers "who can see this" completely, but are changed on the folder that
 * carries them. A grant covers everything beneath its folder, and the person never sees the names of the
 * folders above it.
 */
export function ShareDialog({
  folder, myUserId, myEmail, onClose, onChanged,
}: {
  folder: { id: string; address: string } | null;
  myUserId: string | null;
  myEmail: string;
  onClose: () => void;
  /** Access changed: the folder's Shared badge may have come or gone. */
  onChanged: (folderId: string) => void;
}) {
  const [grants, setGrants] = useState<{ direct: SpaceGrantView[]; inherited: SpaceGrantView[] } | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState('');
  const [access, setAccess] = useState<GrantAccess>('viewer');
  const [expires, setExpires] = useState('');

  const folderId = folder?.id ?? null;

  const load = useCallback(async () => {
    if (!folderId) return;
    try {
      setGrants(await spacesApi.grants(folderId));
      setError(null);
    } catch (err) {
      setError(spaceErrorMessage(err, 'Could not load who has access'));
    }
  }, [folderId]);

  useEffect(() => {
    if (!folderId) return;
    setGrants(null);
    setError(null);
    setUserId('');
    setAccess('viewer');
    setExpires('');
    void load();
    api.users.list().then(setUsers).catch(() => setUsers([]));
  }, [folderId, load]);

  // Only approved accounts: anyone else could not sign in to use the access, and offering a suspended
  // account a folder would read as having shared it.
  const candidates = useMemo(
    () =>
      users
        .filter((u) => u.accountStatus === 'APPROVED' && u.id !== myUserId && u.email.toLowerCase() !== myEmail.toLowerCase())
        .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email)),
    [users, myUserId, myEmail],
  );
  const chosen = candidates.find((u) => u.id === userId) ?? null;
  const existing = chosen ? grants?.direct.find((g) => g.userId === chosen.id) ?? null : null;

  async function act(fn: () => Promise<unknown>) {
    if (!folderId) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged(folderId);
    } catch (err) {
      setError(spaceErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const add = () =>
    chosen &&
    act(async () => {
      await spacesApi.grant(folderId!, { email: chosen.email, access, expiresAt: expires ? endOfDay(expires) : null });
      setUserId('');
      setExpires('');
    });

  // Changing someone's access keeps their expiry: granting again replaces the grant whole.
  const changeAccess = (grant: SpaceGrantView, next: GrantAccess) =>
    grant.email && act(() => spacesApi.grant(folderId!, { email: grant.email!, access: next, expiresAt: grant.expiresAt }));

  const revoke = (grant: SpaceGrantView) => {
    if (!window.confirm(`Remove ${personLabel(grant)}'s access to ${folder?.address}? They lose everything inside it too.`)) return;
    void act(() => spacesApi.revoke(folderId!, grant.userId));
  };

  return (
    <Dialog open={folder !== null} onClose={onClose} title="Share folder" className="max-w-xl">
      <div className="space-y-5">
        <p className="text-xs text-ink-muted">
          <span className="font-mono text-ink-secondary">{folder?.address}</span> and everything in it. Viewers can list, read and
          download; editors can also upload, edit, rename, move and delete inside it. They never see the names of the folders above it.
        </p>

        {error && <div className="rounded-md border border-danger-ring bg-danger-bg px-3 py-2 text-xs text-danger-text">{error}</div>}

        <section>
          <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-2">Who has access</p>
          {grants === null ? (
            <p className="text-xs text-ink-faint">{error ? '' : 'Loading…'}</p>
          ) : grants.direct.length === 0 && grants.inherited.length === 0 ? (
            <p className="text-xs text-ink-faint">Only you.</p>
          ) : (
            <ul className="rounded-md border border-border divide-y divide-border-subtle">
              {grants.direct.map((grant) => (
                <li key={`d-${grant.userId}`} className={cn('flex items-center gap-3 px-3 py-2', grant.expired && 'opacity-70')}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink truncate">{personLabel(grant)}</p>
                    <p className="text-[11px] text-ink-faint truncate flex items-center gap-1.5">
                      {grant.name && grant.email ? <span className="truncate">{grant.email}</span> : null}
                      <Expiry grant={grant} />
                    </p>
                  </div>
                  {/* An expired grant is re-granted from the form below with a new date rather than
                      re-enabled here, where it would silently keep a date already past. */}
                  <Select
                    value={grant.access}
                    disabled={busy || grant.expired || !grant.email}
                    onChange={(e) => void changeAccess(grant, e.target.value as GrantAccess)}
                    className="w-28 py-1 text-xs"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </Select>
                  <button
                    type="button"
                    onClick={() => revoke(grant)}
                    disabled={busy}
                    title="Remove access"
                    className="p-1.5 rounded text-ink-faint hover:text-danger-text hover:bg-danger-bg transition-colors disabled:opacity-40"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
              {grants.inherited.map((grant) => (
                <li key={`i-${grant.userId}-${grant.folderId}`} className={cn('flex items-center gap-3 px-3 py-2 bg-surface-subtle/60', grant.expired && 'opacity-70')}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink-secondary truncate">{personLabel(grant)}</p>
                    <p className="text-[11px] text-ink-faint truncate flex items-center gap-1.5">
                      <span className="truncate">
                        from <span className="font-mono">{grant.folderAddress}</span>
                      </span>
                      <Expiry grant={grant} />
                    </p>
                  </div>
                  <Badge variant={grant.access === 'editor' ? 'info' : 'muted'}>{grant.access}</Badge>
                </li>
              ))}
            </ul>
          )}
          {grants && grants.inherited.length > 0 && (
            <p className="mt-1.5 text-[10px] text-ink-faint">Inherited access is changed on the folder that grants it.</p>
          )}
        </section>

        <section className="rounded-lg border border-border-subtle bg-surface-subtle px-4 py-4">
          <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-2.5">Add a person</p>
          <div className="space-y-2">
            <Select value={userId} onChange={(e) => setUserId(e.target.value)} disabled={busy || candidates.length === 0}>
              <option value="">{candidates.length === 0 ? 'No one else to share with' : 'Choose a person…'}</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ? `${u.name} — ${u.email}` : u.email}
                  {grants?.direct.some((g) => g.userId === u.id) ? ' (has access)' : ''}
                </option>
              ))}
            </Select>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={access} onChange={(e) => setAccess(e.target.value as GrantAccess)} disabled={busy} className="w-32">
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </Select>
              <label className="flex items-center gap-2 text-[11px] text-ink-muted">
                Expires
                <Input type="date" value={expires} min={todayInput()} onChange={(e) => setExpires(e.target.value)} disabled={busy} className="w-40 py-1.5" />
              </label>
              <span className="flex-1" />
              <Button variant="primary" size="sm" onClick={() => void add()} disabled={busy || !chosen}>
                <UserPlus size={12} />
                {existing ? 'Update access' : 'Share'}
              </Button>
            </div>
            {existing && (
              <p className="text-[10px] text-ink-faint">They already have access; this replaces their access and expiry with what is chosen here.</p>
            )}
          </div>
        </section>
      </div>
    </Dialog>
  );
}
